import { Component, forwardRef, Inject, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { NgForm } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { firstValueFrom } from 'rxjs';
import { ALERT_TIMELINE_WIDGET_TAG, ErrorMessages, Permissions } from '../../common/constants';
import { Alert } from '../../model';
import { AuthenticationService } from '../../service/authentication.service';
import { ContextService } from '../../service/context.service';
import { ThingContextService } from '../../service/thing-context.service';
import { ThingService } from '../../service/thing.service';
import { TicketService } from '../../service/ticket.service';
import { UiService } from '../../service/ui.service';
import { WidgetRegistrationService } from '../../service/widget-registration.service';
import { EventSeverityPipe } from '../../shared/pipe/event-severity.pipe';
import { ErrorUtility } from '../../utility/error-utility';
import { SnackbarService } from '../../widget/shared/snackbar.service';
import { TicketListDialogComponent } from '../../widget/ticket-list/ticket-list-dialog.component';
import { AlertWorkSessionDetailsPageDialogComponent } from '../alert-work-session-details-page-dialog/alert-work-session-details-page-dialog.component';
import { AlertService } from '../shared/alert.service';
import { TemplateLoadStatus } from '../template-loader/template-load-status';

@Component({
    selector: 'event-details-page-dialog',
    template: require('./event-details-page-dialog.component.html'),
    styles: [require('./event-details-page-dialog.component.css')]
})
export class EventDetailsPageDialogComponent extends AlertWorkSessionDetailsPageDialogComponent<Alert> implements OnInit, OnDestroy {

    @ViewChild('noteForm') noteForm: NgForm;

    showTicketButton: boolean;
    canWriteNotes: boolean;
    updating: boolean;
    loading = true;
    loadError: string;
    detailsLoadStatus: TemplateLoadStatus;
    timelineLoadStatus: TemplateLoadStatus;

    private data: any;
    private destroyed = false;

    constructor(
        @Inject(forwardRef(() => UiService)) uiService: UiService,
        @Inject(forwardRef(() => ThingService)) thingService: ThingService,
        @Inject(forwardRef(() => ContextService)) context: ContextService,
        @Inject(forwardRef(() => ThingContextService)) thingContextService: ThingContextService,
        @Inject(MAT_DIALOG_DATA) data,
        @Inject(forwardRef(() => MatDialog)) private dialog: MatDialog,
        @Inject(forwardRef(() => AuthenticationService)) private authenticationService: AuthenticationService,
        @Inject(forwardRef(() => TicketService)) private ticketService: TicketService,
        @Inject(forwardRef(() => AlertService)) private alertService: AlertService,
        @Inject(forwardRef(() => SnackbarService)) private snackbarService: SnackbarService,
        @Inject(forwardRef(() => EventSeverityPipe)) private eventSeverityPipe: EventSeverityPipe,
        @Inject(forwardRef(() => WidgetRegistrationService)) private widgetRegistrationService: WidgetRegistrationService
    ) {
        super(uiService, thingService, context, thingContextService, data);
        this.data = data;
    }

    ngOnInit() {
        this.saveCurrentContext();
        this.canWriteNotes = this.authenticationService.hasPermission(Permissions.WRITE_ALERT_STATUS);
        this.init(this.elementId);
    }

    ngOnDestroy(): void {
        this.destroyed = true;
        this.resetContext();
    }

    init(alertId: string): void {
        this.loading = true;
        this.loadError = null;
        // 1. Load the active or historical event; it must belong to a Thing.
        this.getElementById(alertId)
            .then(alert => {
                if (!alert.thing?.id) {
                    throw new Error('Thing context not available');
                }
                // 2. Pick the template of the details section.
                this.element = alert;
                this.resolveTemplate(alert);
                this.handleTicketButtonVisiblity();
                // 3. Make this event's Thing the current context.
                return this.updateContext(alert.thing.id, 'Alerts');
            })
            .then(thing => {
                // 4. If the dialog was closed meanwhile, undo what the Thing loader just wrote in the shared context.
                if (this.destroyed) {
                    this.resetContext();
                    return;
                }
                // 5. The Thing loader returns undefined on error instead of rejecting.
                if (!thing) {
                    throw new Error('Thing context not available');
                }
                this.loading = false;
            })
            .catch(err => this.handleLoadError(err));
    }

    private resolveTemplate(alert: Alert): void {
        // 1. Prefer the template configured on the event definition.
        if (alert.templateName) {
            this.templateName = alert.templateName;
            this.thingDefinitionId = alert.thing?.thingDefinitionId;
        } else {
            // 2. Otherwise use the general event template configured in the UI, if any.
            this.templateName = this.uiService.getEventDetailsTemplate();
            this.thingDefinitionId = null;
        }
        // 3. With no template at all, the Details section shows its empty message.
        this.detailsLoadStatus = this.templateName ? null : TemplateLoadStatus.EMPTY;
    }

    onDetailsLoadStatus(status: TemplateLoadStatus): void {
        this.detailsLoadStatus = status;
    }

    isDetailsEmpty(): boolean {
        return this.detailsLoadStatus === TemplateLoadStatus.EMPTY;
    }

    isDetailsLoading(): boolean {
        return !!this.templateName && this.detailsLoadStatus === null;
    }

    isDetailsError(): boolean {
        return this.detailsLoadStatus === TemplateLoadStatus.ERROR;
    }

    onTimelineLoadStatus(status: TemplateLoadStatus): void {
        this.timelineLoadStatus = status;
    }

    hasTimelineWidget(): boolean {
        return this.widgetRegistrationService.hasWidget(ALERT_TIMELINE_WIDGET_TAG);
    }

    hasTimelineLoadError(): boolean {
        return this.timelineLoadStatus === TemplateLoadStatus.ERROR;
    }

    private handleLoadError(err): void {
        this.loading = false;
        this.loaded = false;
        this.loadError = ErrorUtility.getMessage(err, ErrorMessages.GET_DATA_ERROR);
    }

    protected getElementById(elementId: string): Promise<Alert> {
        if (this.isHistorical) {
            return this.thingService.getHistoricalAlertById(elementId);
        }
        return this.thingService.getAlertById(elementId).catch(() => this.thingService.getHistoricalAlertById(elementId));
    }

    protected resetElementContext(): void {
        this.thingContextService.resetCurrentAlert();
    }

    isNotesSectionVisible(): boolean {
        return !!this.element && (this.canWriteNotes || !!this.element.notes?.trim());
    }

    canEditNotes(): boolean {
        return !!this.element && this.canWriteNotes;
    }

    getEventSeverityHtml(): string {
        return this.eventSeverityPipe.transform(this.element?.eventSeverity);
    }

    showSeverityIcon(): boolean {
        return !this.authenticationService.isNewStyleManagerEnabled();
    }

    isFooterVisible(): boolean {
        return this.updating || (!this.loadError && (this.showTicketButton || this.canEditNotes()));
    }

    private handleTicketButtonVisiblity(): void {
        this.ticketService.getTicketTypes().then(types => {
            this.showTicketButton = !this.data.ticketButtonDisabled && !!this.element.thing?.id && this.authenticationService.hasPermission(Permissions.READ_TICKET) && types.some(t => t.eventSelectionEnabled) && [2, 3, 4].includes(this.element.eventSeverity);
        }).catch(() => this.showTicketButton = false);
    }

    openTicketList() {
        const dialogConfig = new MatDialogConfig();
        dialogConfig.autoFocus = false;
        dialogConfig.data = { thingId: this.element.thing.id, eventId: this.element.id };
        firstValueFrom(this.dialog.open(TicketListDialogComponent, dialogConfig).afterClosed());
    }

    saveNotes(): void {
        this.updating = true;
        const formValue = this.noteForm?.form.getRawValue();
        this.alertService.patchNotes(this.element, formValue.content).then(() => {
            this.element.notes = formValue.content;
            this.updating = false;
            this.noteForm?.form.markAsPristine();
            this.snackbarService.showSnackbar('operationCompletedSuccessfullyProperty');
        }).catch(err => {
            this.snackbarService.showSnackbar(ErrorUtility.getMessage(err, ErrorMessages.SAVE_DATA_ERROR), 'notification-error');
            this.updating = false;
        });
    }
}